import { graphql, useStaticQuery } from "gatsby"
import React from "react"
import styled, { css } from "styled-components"
import BreakpointImage from "../../design-system/atoms/breakpoint-image"
import Button from "../../design-system/atoms/button"
import Block from "../../design-system/atoms/layout-primitives/block"
import Spacer from "../../design-system/atoms/layout-primitives/spacer"
import { T } from "../../design-system/atoms/text"
import ContentBlock from "../../design-system/molecules/content-block"
import IconTitle from "../../design-system/molecules/icon-title"
import {
  FadeIn,
  FadeInBottom,
} from "../../design-system/styles/animations.styles"
import {
  IFluidImageQuery,
  IPublicURLQuery,
} from "../../design-system/types/imageQuery.types"
import withAnimation from "../../design-system/util/withAnimation"
import _FadedTitle from "../shared/faded-title"
import Grid from "../shared/grid"
const FadedTitle = withAnimation(_FadedTitle)
const Span = styled.span`
  display: inline-block;
`
const AnimatedSpan = withAnimation(Span)
const AnimatedBlock = withAnimation(Block)

interface IDataExportStaticQuery {
  mobileMallMap: IFluidImageQuery
  tabletMallMap: IFluidImageQuery
  desktopMallMap: IFluidImageQuery
  mobileImdf: IFluidImageQuery
  desktopImdf: IFluidImageQuery
  tabletImdf: IFluidImageQuery
  exportsFade: IPublicURLQuery
  documentIcon: IPublicURLQuery
}

export default function DataExport() {
  const {
    mobileMallMap,
    tabletMallMap,
    desktopMallMap,
    mobileImdf,
    tabletImdf,
    desktopImdf,
    exportsFade,
    documentIcon,
  } = useStaticQuery<IDataExportStaticQuery>(graphql`
    query {
      mobileMallMap: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/exports_map_noshadow.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 400, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      tabletMallMap: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/mvf tablet.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopMallMap: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/mvf desktop.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 600, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopImdf: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/imdf_desktop.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 559, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      tabletImdf: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/imdf_tablet.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 532, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobileImdf: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Mobile/imdf_mobile.png"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 428, quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      exportsFade: file(
        relativePath: {
          eq: "1_SOLUTIONS/Developer Tools/Desktop/fade_titles/exports_fade.svg"
        }
      ) {
        publicURL
      }
      documentIcon: file(
        relativePath: { eq: "1_SOLUTIONS/Developer Tools/document.svg" }
      ) {
        publicURL
      }
    }
  `)

  return (
    <>
      <Spacer height={{ sm: "32px", md: "64px", lg: "96px" }} />
      <Grid>
        {/* MVF Export */}
        <AnimatedBlock
          animation={FadeIn}
          col={{ sm: "1/-1", md: "2/-2", lg: "-6/-1" }}
          row={{ md: "2", lg: "1" }}
          variants={[
            css`
              box-shadow: 0px 4.65248px 23.2624px rgba(0, 0, 0, 0.07);
              border-radius: 2px;
            `,
            css`
              margin-top: 48px;
            `,
            css`
              margin-top: 0;
              align-self: center;
            `,
          ]}
        >
          <BreakpointImage
            imageVariants={{
              sm: mobileMallMap.childImageSharp.fluid,
              md: tabletMallMap.childImageSharp.fluid,
              lg: desktopMallMap.childImageSharp.fluid,
            }}
            imageStyle={{ objectFit: "cover" }}
          />
        </AnimatedBlock>
        <Block
          col={{ sm: "1/-1", md: "2/-2", lg: "2/7" }}
          row={{ md: "1" }}
          variants={[
            css`
              margin-top: 32px;
              text-align: center;
            `,
            css`
              margin-top: 0;
            `,
            css`
              text-align: left;
            `,
          ]}
        >
          <ContentBlock.H2
            title={
              <>
                <FadedTitle
                  animation={FadeIn}
                  fadeSettings={{ lg: "left" }}
                  image={exportsFade.publicURL}
                  title={
                    <>
                      Mappedin <span>Data Export</span>
                    </>
                  }
                  alt={
                    "The word 'exports' displayed in a faded style being the title"
                  }
                  textAlign={{ sm: "center" }}
                />
                <IconTitle
                  icon={
                    <img src={documentIcon.publicURL} alt="Document Icon" />
                  }
                  title="IMDF COMPATIBLE"
                  alignmentVariants={[
                    css`
                      padding-top: 16px;
                      justify-content: center;
                    `,
                    css`
                      justify-content: center;
                    `,
                    css`
                      padding-top: 24px;
                      justify-content: left;
                    `,
                  ]}
                  iconVariants={[
                    css`
                      width: auto;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      & > img {
                        height: 16px;
                      }
                    `,
                    null,
                    css`
                      & > img {
                        height: 24px;
                      }
                    `,
                  ]}
                  textVariants={[
                    css`
                      ${T.body.small`400`};
                      color: var(--burnt-orange);
                      letter-spacing: 0.08em;
                    `,
                    null,
                    css`
                      ${T.body.feature`300`};
                    `,
                  ]}
                />
              </>
            }
            description={
              <AnimatedSpan animation={FadeInBottom}>
                Mappedin’s GeoJSON based format provides customers with geometry
                and location data associated with a venue that is directly
                connected to the Mappedin CMS. Along with being IMDF compatible,
                this export includes nodes and paths, allowing you to easily
                build wayfinding capabilities.
              </AnimatedSpan>
            }
            titleVariants={[
              css`
                * > span {
                  /* On small screens it looks better on a new line */
                  display: block;
                }
              `,
              css`
                * > span {
                  display: inline;
                }
              `,
            ]}
          />
          <Spacer height={{ sm: "32px", md: "48px" }} />
          <AnimatedBlock
            animation={FadeInBottom}
            col={{ sm: "1/-1" }}
            display={{ sm: "grid" }}
            variants={[
              css`
                justify-items: center;
              `,
              null,
              css`
                justify-items: left;
              `,
            ]}
          >
            <Button.Link
              to="https://www.mappedin.com/mapping/mvf-export/"
              target="_blank"
            >
              Learn More
            </Button.Link>
          </AnimatedBlock>
        </Block>
        <Spacer height={{ sm: "64px", lg: "128px" }} />
        {/* IMDF EXPORT */}
        <AnimatedBlock
          animation={FadeIn}
          col={{ sm: "1/-1", md: "2/-2", lg: "2/7" }}
          row={{ md: "5", lg: "4" }}
          variants={[
            css`
              box-shadow: 0px 4.65248px 23.2624px rgba(0, 0, 0, 0.07);
              border-radius: 2px;
            `,
            css`
              margin-top: 48px;
            `,
            css`
              margin-top: 0;
              align-self: center;
            `,
          ]}
        >
          <BreakpointImage
            imageVariants={{
              sm: mobileImdf.childImageSharp.fluid,
              md: tabletImdf.childImageSharp.fluid,
              lg: desktopImdf.childImageSharp.fluid,
            }}
            imageStyle={{ objectFit: "cover" }}
          />
        </AnimatedBlock>
        <Block
          col={{ sm: "1/-1", md: "2/-2", lg: "-6/-1" }}
          row={{ md: "4" }}
          variants={[
            css`
              margin-top: 32px;
              text-align: center;
            `,
            css`
              margin-top: 0;
            `,
            css`
              text-align: left;
            `,
          ]}
        >
          <ContentBlock.H2
            title={
              <FadedTitle
                animation={FadeIn}
                fadeSettings={{ lg: "left" }}
                image={exportsFade.publicURL}
                title={<>IMDF Export</>}
                alt={
                  "The word 'exports' displayed in a faded style being the title"
                }
                textAlign={{ sm: "center" }}
              />
            }
            description={
              <AnimatedSpan animation={FadeInBottom}>
                Indoor Mapping Data Format (IMDF) makes it possible for anyone
                to create indoor map apps and services using the same highly
                accurate and detailed data across any website, app or operating
                system. Mappedin’s IMDF export has been fully approved through
                Apple, following the recognized Community Standard.
              </AnimatedSpan>
            }
          />
          <Spacer height={{ sm: "32px", md: "48px" }} />
          <AnimatedBlock
            animation={FadeInBottom}
            col={{ sm: "1/-1" }}
            display={{ sm: "grid" }}
            variants={[
              css`
                justify-items: center;
              `,
              null,
              css`
                justify-items: left;
              `,
            ]}
          >
            <Button.Link
              to="https://www.mappedin.com/mapping/imdf-export/"
              target="_blank"
            >
              Learn More
            </Button.Link>
          </AnimatedBlock>
        </Block>
      </Grid>
      <Spacer height={{ sm: "48px", md: "64px", lg: "96px" }} />
    </>
  )
}
